import { AppStrings } from "../../../../../../utils/constants/AppStrings";

// Reusable horizontal tier stepper shared across monthly challenge types.
// `milestones` describe each node (threshold + reward + claim state).
// `reachedCount` drives node styling; `count`/`goal` drive the continuous
// green fill so the bar grows between tiers (FE-2544).

const clampReached = (reachedCount, total) =>
  Math.max(0, Math.min(reachedCount, total));

const clampPercent = (value) => Math.max(0, Math.min(100, value));

/** Center of the i-th equal flex column, in percent of the track width —
 *  where the tier node dots actually render. */
const nodeCenterPercent = (index, total) => ((index + 0.5) / total) * 100;

/**
 * Continuous fill aligned with the tier nodes. Nodes sit at equal flex-column
 * centers (not at threshold-proportional positions), so a plain
 * `count / goal` fill crosses nodes before their thresholds are met. Instead,
 * interpolate count between node centers segment by segment:
 *   - below the first threshold → 0 → first node center
 *   - between thresholds → between the two node centers
 *   - at/over the final threshold → 100% (completed bar reads as full)
 * Falls back to snapping to the last reached node when count/thresholds
 * aren't usable (read-only cards).
 */
export const getFillPercent = ({
  count,
  goal,
  reachedCount = 0,
  total = 0,
  milestones = [],
} = {}) => {
  if (total <= 0) {
    return 0;
  }

  const numericCount = Number(count);
  const thresholds = milestones.map((milestone) =>
    Number(milestone?.threshold),
  );
  const thresholdsUsable =
    thresholds.length === total &&
    thresholds.every(
      (threshold, index) =>
        Number.isFinite(threshold) &&
        threshold > 0 &&
        (index === 0 || threshold > thresholds[index - 1]),
    );

  if (Number.isFinite(numericCount) && thresholdsUsable) {
    if (numericCount <= 0) {
      return 0;
    }
    if (numericCount >= thresholds[total - 1]) {
      return 100;
    }
    // Segment the count falls in: from the previous threshold (or 0) to the
    // next one, mapped onto the corresponding node-center positions.
    let segment = 0;
    while (numericCount >= thresholds[segment]) {
      segment += 1;
    }
    const segmentStartCount = segment === 0 ? 0 : thresholds[segment - 1];
    const segmentStartPercent =
      segment === 0 ? 0 : nodeCenterPercent(segment - 1, total);
    const segmentEndPercent = nodeCenterPercent(segment, total);
    const segmentFraction =
      (numericCount - segmentStartCount) /
      (thresholds[segment] - segmentStartCount);
    return clampPercent(
      segmentStartPercent +
        segmentFraction * (segmentEndPercent - segmentStartPercent),
    );
  }

  // Linear fallback when thresholds are unusable but a goal exists.
  const numericGoal =
    Number(goal) ||
    Number(milestones[total - 1]?.threshold) ||
    0;
  if (Number.isFinite(numericCount) && numericGoal > 0) {
    return clampPercent((numericCount / numericGoal) * 100);
  }

  // Fallback: fill to the center of the last reached column so Tier 1 isn't
  // stuck at width 0 (read-only cards without a count).
  if (total === 1) {
    return reachedCount > 0 ? 100 : 0;
  }
  const safeReached = clampReached(reachedCount, total);
  if (safeReached <= 0) {
    return 0;
  }
  return clampPercent(nodeCenterPercent(safeReached - 1, total));
};

const MonthlyTierProgressBar = ({
  progressLabel,
  challengesText,
  percentLabel,
  milestones = [],
  reachedCount = 0,
  count,
  goal,
  onEditProgress,
  testId = "monthly-tier-progress",
}) => {
  const total = milestones.length;
  const safeReached = clampReached(reachedCount, total);
  const fillPercent = getFillPercent({
    count,
    goal,
    reachedCount,
    total,
    milestones,
  });
  const ariaNow = Number.isFinite(Number(count))
    ? Number(count)
    : safeReached;
  const ariaMax =
    Number(goal) ||
    Number(milestones[total - 1]?.threshold) ||
    total;

  return (
    <div
      className="user-manager-game-pass-monthly-tier"
      data-testid={testId}
    >
      <div className="user-manager-game-pass-monthly-tier-head">
        <div className="user-manager-game-pass-monthly-tier-head-left">
          <span className="user-manager-game-pass-monthly-tier-label">
            {progressLabel}
          </span>
          {onEditProgress && (
            <button
              type="button"
              className="user-manager-game-pass-monthly-tier-edit"
              onClick={onEditProgress}
              data-testid={`${testId}-edit`}
            >
              {AppStrings.USER_GAME_PASS_MONTHLY_EDIT_PROGRESS}
            </button>
          )}
        </div>
        {(challengesText || percentLabel) && (
          <div className="user-manager-game-pass-monthly-tier-head-right">
            {challengesText && (
              <span
                className="user-manager-game-pass-monthly-tier-challenges"
                data-testid={`${testId}-challenges`}
              >
                {challengesText}
              </span>
            )}
            {percentLabel && (
              <span
                className="user-manager-game-pass-monthly-tier-percent"
                data-testid={`${testId}-percent`}
              >
                {percentLabel}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="user-manager-game-pass-monthly-tier-track-wrap">
        <div
          className="user-manager-game-pass-monthly-tier-track"
          role="progressbar"
          aria-label={progressLabel}
          aria-valuenow={ariaNow}
          aria-valuemin={0}
          aria-valuemax={ariaMax}
        >
          <div
            className="user-manager-game-pass-monthly-tier-fill"
            style={{ width: `${fillPercent}%` }}
            data-testid={`${testId}-fill`}
            data-fill-percent={fillPercent}
          />
        </div>
        <div className="user-manager-game-pass-monthly-tier-nodes">
          {milestones.map((milestone, index) => {
            const reached = index < safeReached;
            const claimedLabel = milestone.claimed
              ? AppStrings.USER_GAME_PASS_MONTHLY_REWARD_CLAIMED
              : AppStrings.USER_GAME_PASS_MONTHLY_REWARD_NOT_CLAIMED;
            return (
              <div
                key={milestone.threshold}
                className="user-manager-game-pass-monthly-tier-node-col"
                data-testid={`${testId}-node-${index}`}
              >
                <span
                  className={`user-manager-game-pass-monthly-tier-node${
                    reached
                      ? " user-manager-game-pass-monthly-tier-node--reached"
                      : ""
                  }`}
                  data-testid={`${testId}-node-dot-${index}`}
                >
                  {milestone.threshold}
                </span>
                <span className="user-manager-game-pass-monthly-tier-reward">
                  <span className="user-manager-game-pass-monthly-tier-reward-amount">
                    {milestone.rewardLabel}
                  </span>
                  <span
                    className={`user-manager-game-pass-monthly-tier-reward-state${
                      milestone.claimed
                        ? " user-manager-game-pass-monthly-tier-reward-state--claimed"
                        : ""
                    }`}
                  >
                    {claimedLabel}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MonthlyTierProgressBar;
